import { useState, useEffect } from 'react';

/**
 * Module-level scoped singleton: callers can share a scope to avoid
 * scheduling duplicate idle work, while still allowing independent
 * timing between different carousel surfaces.
 */
type ScopeState = {
  hasEnabled: boolean;
  isScheduleStarted: boolean;
  listeners: Set<() => void>;
};

const DEFAULT_SCOPE = 'global';
const scopeStateMap = new Map<string, ScopeState>();

function getScopeState(scope: string): ScopeState {
  const existing = scopeStateMap.get(scope);
  if (existing) return existing;

  const created: ScopeState = {
    hasEnabled: false,
    isScheduleStarted: false,
    listeners: new Set<() => void>(),
  };
  scopeStateMap.set(scope, created);
  return created;
}

function subscribeToDeferredReady(scope: string, listener: () => void) {
  const state = getScopeState(scope);
  state.listeners.add(listener);
  return () => state.listeners.delete(listener);
}

function notifyDeferredReadyEnabled(scope: string) {
  const state = getScopeState(scope);
  state.hasEnabled = true;
  state.listeners.forEach(listener => listener());
  state.listeners.clear();
}

function scheduleDeferredReady(scope: string, delayMs = 0) {
  if (typeof window === 'undefined') return;
  const state = getScopeState(scope);
  if (state.hasEnabled || state.isScheduleStarted) return;

  state.isScheduleStarted = true;
  const onReady = () => notifyDeferredReadyEnabled(scope);

  const scheduleIdle = () => {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(onReady);
      return;
    }
    window.setTimeout(onReady, 1200);
  };

  if (delayMs > 0) {
    window.setTimeout(scheduleIdle, delayMs);
  } else {
    scheduleIdle();
  }
}

interface DeferredReadyOptions {
  /** Skip deferral entirely — immediately returns `true`. */
  skip?: boolean;
  /**
   * Scheduling scope key.
   *
   * Callers that share the same scope also share one idle schedule/state.
   * Use distinct scopes for independent timing behavior.
   * @default "global"
   */
  scope?: string;
  /**
   * Milliseconds to wait **before** scheduling `requestIdleCallback`.
   * Useful for ensuring the idle check doesn't fire during the critical
   * rendering window even if the browser briefly goes idle.
   *
   * Only the first consumer's `delayMs` takes effect (singleton).
   * @default 0
   */
  delayMs?: number;
}

/**
 * Defers readiness until the browser is idle.
 *
 * Returns `true` once the browser has been idle (or the fallback timeout fires).
 * Consumers in the same scope share a single scheduler.
 */
export function useDeferredReady(options: DeferredReadyOptions = {}): boolean {
  const { skip = false, delayMs = 0, scope = DEFAULT_SCOPE } = options;
  const [isReady, setIsReady] = useState(() => getScopeState(scope).hasEnabled);

  useEffect(() => {
    if (skip) {
      setIsReady(true);
      return;
    }

    const state = getScopeState(scope);
    if (state.hasEnabled) {
      setIsReady(true);
      return;
    }

    const unsubscribe = subscribeToDeferredReady(scope, () => {
      setIsReady(true);
    });
    scheduleDeferredReady(scope, delayMs);

    return () => {
      unsubscribe();
    };
  }, [skip, delayMs, scope]);

  return isReady;
}
